// ============================================
// Admin Sidebar Navigation Config
// Ported from: layout/sidebar.php (1,391 lines)
// Permission-aware, module-aware, i18n-ready
// COMPLETE 1:1 match with PHP sidebar
// ============================================
import type { ReactNode } from 'react';

export interface NavItem {
  key: string;
  labelKey: string;           // i18n key from system_lang.php
  icon?: string;              // Icon name (optional for children)
  path?: string;              // Route path (if leaf node)
  module?: string;            // Module key from modules table
  permissions?: string[];     // Required permissions (any match = show)
  permissionAction?: string;  // Default: 'can_view'
  superAdminOnly?: boolean;   // Only show for Super Admin role
  children?: NavItem[];       // Sub-menu items
  badge?: string;             // Dynamic badge (e.g., unread count)
}

// ─── Admin Sidebar Menu ───
// 25 top-level groups — exact match to old PHP sidebar.php
export const ADMIN_NAV: NavItem[] = [
  // Dashboard (always visible)
  { key: 'dashboard', labelKey: 'dashboard', icon: 'home', path: '/dashboard' },

  // ═══════════════ Front Office ═══════════════
  // PHP line 18-84: module_lib->hasActive('front_office')
  {
    key: 'front_office', labelKey: 'front_office', icon: 'users', module: 'front_office', path: '/front-office',
    permissions: ['admission_enquiry', 'visitor_book', 'phon_call_log', 'postal_dispatch', 'postal_receive', 'complaint', 'setup_font_office'],
    children: [
      { key: 'fo_dashboard', labelKey: 'dashboard', path: '/front-office', permissions: ['admission_enquiry'] },
      { key: 'enquiry', labelKey: 'admission_enquiry', path: '/front-office/enquiry', permissions: ['admission_enquiry'] },
      { key: 'visitors', labelKey: 'visitor_book', path: '/front-office/visitors', permissions: ['visitor_book'] },
      { key: 'phone_call', labelKey: 'phone_call_log', path: '/front-office/phone-call', permissions: ['phon_call_log'] },
      { key: 'postal_dispatch', labelKey: 'postal_dispatch', path: '/front-office/postal-dispatch', permissions: ['postal_dispatch', 'postal_receive'] },
      { key: 'complaint', labelKey: 'complain', path: '/front-office/complaint', permissions: ['complaint'] },
      { key: 'setup_front_office', labelKey: 'setup_front_office', path: '/front-office/setup', permissions: ['setup_font_office'] },
    ],
  },

  // ═══════════════ Student Information ═══════════════
  // PHP line 86-171: module_lib->hasActive('student_information')
  {
    key: 'student_info', labelKey: 'student_information', icon: 'user', module: 'student_information',
    permissions: ['student', 'student_categories', 'student_houses', 'disable_student', 'online_admission', 'multiclass_student', 'disable_reason'],
    children: [
      { key: 'student_details', labelKey: 'student_details', path: '/students/search', permissions: ['student'] },
      { key: 'student_admission', labelKey: 'student_admission', path: '/students/create', permissions: ['student'], permissionAction: 'can_add' },
      { key: 'online_admission', labelKey: 'online_admission', path: '/students/online', permissions: ['online_admission'], module: 'online_admission' },
      { key: 'disabled_students', labelKey: 'disabled_students', path: '/students/disabled', permissions: ['disable_student'] },
      { key: 'multiclass_student', labelKey: 'multi_class_student', path: '/students/multi-class', permissions: ['multiclass_student'], module: 'multi_class' },
      { key: 'bulk_delete', labelKey: 'bulk_delete', path: '/students/bulk-delete', permissions: ['student'], permissionAction: 'can_delete' },
      { key: 'student_categories', labelKey: 'student_categories', path: '/students/categories', permissions: ['student_categories'] },
      { key: 'student_house', labelKey: 'student_house', path: '/students/house', permissions: ['student_houses'] },
      { key: 'disable_reason', labelKey: 'disable_reason', path: '/students/disable-reason', permissions: ['disable_reason'] },
    ],
  },

  // ═══════════════ Fees Collection ═══════════════
  // PHP line 173-244: module_lib->hasActive('fees_collection')
  {
    key: 'fees', labelKey: 'fees_collection', icon: 'dollar', module: 'fees_collection',
    permissions: ['collect_fees', 'search_fees_payment', 'search_due_fees', 'fees_master', 'fees_group', 'fees_type', 'fees_discount', 'fees_carry_forward', 'fees_reminder', 'accountants'],
    children: [
      { key: 'collect_fees', labelKey: 'collect_fees', path: '/fees/collect', permissions: ['collect_fees'] },
      { key: 'search_fees', labelKey: 'search_fees_payment', path: '/fees/search', permissions: ['search_fees_payment'] },
      { key: 'search_due_fees', labelKey: 'search_due_fees', path: '/fees/due', permissions: ['search_due_fees'] },
      { key: 'fees_master', labelKey: 'fees_master', path: '/fees/master', permissions: ['fees_master'] },
      { key: 'fee_group', labelKey: 'fees_group', path: '/fees/group', permissions: ['fees_group'] },
      { key: 'fee_type', labelKey: 'fees_type', path: '/fees/type', permissions: ['fees_type'] },
      { key: 'fee_discount', labelKey: 'fees_discount', path: '/fees/discount', permissions: ['fees_discount'] },
      { key: 'fee_carry_forward', labelKey: 'fee_carry_forward', path: '/fees/carry-forward', permissions: ['fees_carry_forward'] },
      { key: 'fee_reminder', labelKey: 'fee_reminder', path: '/fees/reminder', permissions: ['fees_reminder'] },
    ],
  },

  // ═══════════════ Income ═══════════════
  // PHP line 245-273: module_lib->hasActive('income')
  {
    key: 'income', labelKey: 'income', icon: 'bar-chart', module: 'income',
    permissions: ['income', 'search_income', 'income_head'],
    children: [
      { key: 'add_income', labelKey: 'add_income', path: '/income/add', permissions: ['income'] },
      { key: 'search_income', labelKey: 'search_income', path: '/income/search', permissions: ['search_income'] },
      { key: 'income_head', labelKey: 'income_head', path: '/income/head', permissions: ['income_head'] },
    ],
  },

  // ═══════════════ Expense ═══════════════
  // PHP line 274-301: module_lib->hasActive('expense')
  {
    key: 'expense', labelKey: 'expenses', icon: 'dollar', module: 'expense',
    permissions: ['expense', 'search_expense', 'expense_head'],
    children: [
      { key: 'add_expense', labelKey: 'add_expense', path: '/expense/add', permissions: ['expense'] },
      { key: 'search_expense', labelKey: 'search_expense', path: '/expense/search', permissions: ['search_expense'] },
      { key: 'expense_head', labelKey: 'expense_head', path: '/expense/head', permissions: ['expense_head'] },
    ],
  },

  // ═══════════════ Attendance ═══════════════
  // PHP line 303-355: module_lib->hasActive('student_attendance')
  {
    key: 'attendance', labelKey: 'attendance', icon: 'check-circle', module: 'student_attendance',
    permissions: ['student_attendance', 'attendance_by_date', 'approve_leave'],
    children: [
      { key: 'student_attendance', labelKey: 'student_attendance', path: '/attendance/student', permissions: ['student_attendance'] },
      { key: 'qr_attendance', labelKey: 'qr_attendance', path: '/attendance/qr', permissions: ['student_attendance'] },
      { key: 'attendance_by_date', labelKey: 'attendance_by_date', path: '/attendance/by-date', permissions: ['attendance_by_date'] },
      { key: 'class_attendance_report', labelKey: 'attendance_report', path: '/attendance/class-report', permissions: ['student_attendance'] },
      { key: 'attendance_analytics', labelKey: 'analytics', path: '/attendance/analytics', permissions: ['student_attendance'] },
      { key: 'biometric_logs', labelKey: 'biometric_logs', path: '/attendance/biometric-logs', permissions: ['student_attendance'] },
      { key: 'approve_leave', labelKey: 'approve_leave', path: '/attendance/leave', permissions: ['approve_leave'] },
    ],
  },

  // ═══════════════ Examinations ═══════════════
  // PHP line 356-413: module_lib->hasActive('examination')
  {
    key: 'examinations', labelKey: 'examinations', icon: 'file', module: 'examination',
    permissions: ['exam_group', 'exam_result', 'design_admit_card', 'print_admit_card', 'design_marksheet', 'print_marksheet', 'marks_grade'],
    children: [
      { key: 'exam_group', labelKey: 'exam_group', path: '/exam/group', permissions: ['exam_group'] },
      { key: 'exam_schedule', labelKey: 'exam_schedule', path: '/exam/schedule' },
      { key: 'marks_entry', labelKey: 'marks', path: '/exam/marks', permissions: ['exam_result'] },
      { key: 'exam_result', labelKey: 'exam_result', path: '/exam/result', permissions: ['exam_result'] },
      { key: 'design_admit_card', labelKey: 'design_admit_card', path: '/exam/admit-card-design', permissions: ['design_admit_card'] },
      { key: 'print_admit_card', labelKey: 'print_admit_card', path: '/exam/admit-card-print', permissions: ['print_admit_card'] },
      { key: 'design_marksheet', labelKey: 'design_marksheet', path: '/exam/marksheet-design', permissions: ['design_marksheet'] },
      { key: 'print_marksheet', labelKey: 'print_marksheet', path: '/exam/marksheet-print', permissions: ['print_marksheet'] },
      { key: 'marks_grade', labelKey: 'marks_grade', path: '/exam/grade', permissions: ['marks_grade'] },
    ],
  },

  // ═══════════════ Online Examinations ═══════════════
  // PHP line 416-449: module_lib->hasActive('online_examination')
  {
    key: 'online_exam', labelKey: 'online_examination', icon: 'globe', module: 'online_examination',
    permissions: ['online_examination', 'question_bank'],
    children: [
      { key: 'online_exam_list', labelKey: 'online_exam', path: '/online-exam/list', permissions: ['online_examination'] },
      { key: 'question_bank', labelKey: 'question_bank', path: '/online-exam/questions', permissions: ['question_bank'] },
      { key: 'practice_analytics', labelKey: 'practice_analytics', path: '/online-exam/practice', permissions: ['online_examination'] },
      { key: 'bulk_progress_report', labelKey: 'bulk_progress_report', path: '/exam/progress-report-bulk', permissions: ['online_examination'] },
      { key: 'online_exam_settings', labelKey: 'settings', path: '/online-exam/settings', permissions: ['online_exam_settings'], icon: 'cog' },
    ],
  },

  // ═══════════════ Academics ═══════════════
  // PHP line 476-547: module_lib->hasActive('academics')
  {
    key: 'academics', labelKey: 'academics', icon: 'book', module: 'academics',
    permissions: ['class_timetable', 'teachers_time_table', 'assign_class_teacher', 'promote_student', 'subject_group', 'subject', 'class', 'section'],
    children: [
      { key: 'class_timetable', labelKey: 'class_timetable', path: '/academics/class-timetable', permissions: ['class_timetable'] },
      { key: 'teachers_timetable', labelKey: 'teachers_timetable', path: '/academics/teacher-timetable', permissions: ['teachers_time_table'] },
      // assign_class_teacher — merged into ClassesPage, route redirects
      // assign_subject_teacher — merged into ClassesPage, route redirects
      { key: 'promote_student', labelKey: 'promote_students', path: '/academics/promote', permissions: ['promote_student'] },
      { key: 'subject_group', labelKey: 'subject_group', path: '/academics/subject-groups', permissions: ['subject_group'] },
      { key: 'subject', labelKey: 'subjects', path: '/academics/subjects', permissions: ['subject'] },
      { key: 'class', labelKey: 'class', path: '/academics/classes', permissions: ['class'] },
      { key: 'section', labelKey: 'sections', path: '/academics/sections', permissions: ['section'] },
    ],
  },

  // ═══════════════ Homework ═══════════════
  // PHP line 766-781: module_lib->hasActive('homework')
  {
    key: 'homework', labelKey: 'homework', icon: 'edit', module: 'homework', permissions: ['homework'],
    children: [
      { key: 'homework_list', labelKey: 'homework_list', path: '/homework', permissions: ['homework'] },
      { key: 'homework_settings', labelKey: 'homework_settings', path: '/homework/settings', permissions: ['homework'] },
    ],
  },

  // ═══════════════ Lesson Plan ═══════════════
  // PHP line 783-826: module_lib->hasActive('lesson_plan')
  {
    key: 'lesson_plan', labelKey: 'lesson_plan', icon: 'clipboard', module: 'lesson_plan',
    permissions: ['manage_lesson_plan', 'manage_syllabus_status', 'lesson', 'topic'],
    children: [
      { key: 'manage_lesson_plan', labelKey: 'manage_lesson_plan', path: '/lesson-plan/manage', permissions: ['manage_lesson_plan'] },
      { key: 'manage_syllabus_status', labelKey: 'manage_syllabus_status', path: '/lesson-plan/syllabus-status', permissions: ['manage_syllabus_status'] },
      { key: 'lesson', labelKey: 'lesson', path: '/lesson-plan/lessons', permissions: ['lesson'] },
      { key: 'topic', labelKey: 'topic', path: '/lesson-plan/topics', permissions: ['topic'] },
      { key: 'lesson_plan_analytics', labelKey: 'analytics', path: '/lesson-plan/analytics', permissions: ['manage_lesson_plan'] },
    ],
  },

  // ═══════════════ AI Home Tutor ═══════════════
  {
    key: 'ai_home_tutor', labelKey: 'AI Home Tutor', icon: 'cpu', module: 'ai_home_tutor',
    permissions: ['ai_home_tutor'],
    children: [
      { key: 'ai_tutor_analytics', labelKey: 'Analytics Dashboard', path: '/ai-tutor/analytics', permissions: ['ai_home_tutor'] },
      { key: 'ai_tutor_exam_modes', labelKey: 'Exam Modes', path: '/ai-tutor/exam-modes', permissions: ['ai_home_tutor'] },
      { key: 'ai_tutor_approval', labelKey: 'Doubt Approval', path: '/ai-tutor/approval', permissions: ['ai_home_tutor'] },
    ],
  },

  // ═══════════════ Human Resource ═══════════════
  // PHP line 551-631: module_lib->hasActive('human_resource')
  {
    key: 'hr', labelKey: 'human_resource', icon: 'users', module: 'human_resource',
    permissions: ['staff', 'organization', 'staff_attendance', 'biometric_management', 'attendance_advanced', 'staff_payroll', 'payroll_settings', 'statutory_report', 'approve_leave_request', 'apply_leave', 'leave_types', 'teachers_rating', 'department', 'designation', 'disable_staff', 'hr_notifications'],
    children: [
      { key: 'staff_directory', labelKey: 'staff_directory', path: '/hr/staff', permissions: ['staff'] },
      { key: 'organization', labelKey: 'organization', path: '/hr/organization', permissions: ['organization'] },
      { key: 'staff_attendance', labelKey: 'staff_attendance', path: '/hr/attendance', permissions: ['staff_attendance'] },
      { key: 'biometric_management', labelKey: 'biometric_management', path: '/hr/biometric-management', permissions: ['biometric_management'] },
      { key: 'attendance_advanced', labelKey: 'attendance_advanced', path: '/hr/attendance-advanced', permissions: ['attendance_advanced'] },
      { key: 'payroll', labelKey: 'payroll', path: '/hr/payroll', permissions: ['staff_payroll'] },
      { key: 'payroll_settings', labelKey: 'payroll_settings', path: '/hr/payroll-settings', permissions: ['payroll_settings'] },
      { key: 'statutory_report', labelKey: 'statutory_report', path: '/hr/statutory-report', permissions: ['statutory_report'] },
      { key: 'approve_leave_request', labelKey: 'approve_leave_request', path: '/hr/approve-leave', permissions: ['approve_leave_request'] },
      { key: 'apply_leave', labelKey: 'apply_leave', path: '/hr/apply-leave', permissions: ['apply_leave'] },
      { key: 'leave_type', labelKey: 'leave_type', path: '/hr/leave-type', permissions: ['leave_types'] },
      { key: 'teachers_rating', labelKey: 'teachers_rating', path: '/hr/rating', permissions: ['teachers_rating'] },
      { key: 'department', labelKey: 'department', path: '/hr/department', permissions: ['department'] },
      { key: 'designation', labelKey: 'designation', path: '/hr/designation', permissions: ['designation'] },
      { key: 'disabled_staff', labelKey: 'disabled_staff', path: '/hr/disabled', permissions: ['disable_staff'] },
      { key: 'hr_notifications', labelKey: 'hr_notifications', path: '/hr/notifications', permissions: ['hr_notifications'] },
    ],
  },


  // ═══════════════ Communicate ═══════════════
  // PHP line 633-671: module_lib->hasActive('communicate')
  {
    key: 'communication', labelKey: 'communicate', icon: 'mail', module: 'communicate', path: '/communication/dashboard',
    permissions: ['notice_board', 'email', 'sms', 'email_sms_log'],
    children: [
      { key: 'comm_dashboard', labelKey: 'dashboard', path: '/communication/dashboard', permissions: ['notice_board'] },
      { key: 'notice_board', labelKey: 'notice_board', path: '/communication/notice', permissions: ['notice_board'] },
      { key: 'send_email', labelKey: 'send_email', path: '/communication/email', permissions: ['email'] },
      { key: 'send_sms', labelKey: 'send_sms', path: '/communication/sms', permissions: ['sms'] },
      { key: 'email_sms_log', labelKey: 'email_sms_log', path: '/communication/log', permissions: ['email_sms_log'] },
      { key: 'notification_settings', labelKey: 'notification_setting', path: '/communication/settings', permissions: ['notice_board'] },
    ],
  },

  // ═══════════════ Download Center ═══════════════
  // PHP line 744-764: module_lib->hasActive('download_center')
  {
    key: 'download_center', labelKey: 'download_center', icon: 'download', module: 'download_center', path: '/downloads/dashboard',
    permissions: ['upload_content'],
    children: [
      { key: 'dl_dashboard', labelKey: 'dashboard', path: '/downloads/dashboard', permissions: ['upload_content'] },
      { key: 'upload_content', labelKey: 'upload_content', path: '/downloads/upload', permissions: ['upload_content'] },
      { key: 'assignments', labelKey: 'assignments', path: '/downloads/assignments' },
      { key: 'study_material', labelKey: 'study_material', path: '/downloads/study-material' },
      { key: 'syllabus_dl', labelKey: 'syllabus', path: '/downloads/syllabus' },
      { key: 'other_downloads', labelKey: 'other_downloads', path: '/downloads/other' },
    ],
  },

  // ═══════════════ Library ═══════════════
  // PHP line 782-815: module_lib->hasActive('library')
  {
    key: 'library', labelKey: 'library', icon: 'book', module: 'library', path: '/library/dashboard',
    permissions: ['books', 'issue_return', 'add_staff_member', 'add_student'],
    children: [
      { key: 'lib_dashboard', labelKey: 'dashboard', path: '/library/dashboard', permissions: ['books'] },
      { key: 'lib_add_book', labelKey: 'add_book', path: '/library/add-book', permissions: ['books'] },
      { key: 'lib_book_list', labelKey: 'book_list', path: '/library/books', permissions: ['books'] },
      { key: 'lib_issue_return', labelKey: 'issue_return', path: '/library/issue-return', permissions: ['issue_return'] },
      { key: 'lib_members', labelKey: 'add_member', path: '/library/members', permissions: ['add_staff_member', 'add_student'] },
      { key: 'lib_reports', labelKey: 'reports', path: '/library/reports', permissions: ['books'] },
      { key: 'lib_settings', labelKey: 'settings', path: '/library/settings', permissions: ['books'] },
    ],
  },

  // ═══════════════ Inventory ═══════════════
  // PHP line 817-863: module_lib->hasActive('inventory')
  {
    key: 'inventory', labelKey: 'inventory', icon: 'folder', module: 'inventory', path: '/inventory/dashboard',
    permissions: ['issue_item', 'item_stock', 'item', 'item_category', 'store', 'supplier'],
    children: [
      { key: 'inv_dashboard', labelKey: 'dashboard', path: '/inventory/dashboard', permissions: ['item'] },
      { key: 'inv_categories', labelKey: 'item_categories', path: '/inventory/categories', permissions: ['item_category'] },
      { key: 'inv_stores', labelKey: 'item_stores', path: '/inventory/stores', permissions: ['store'] },
      { key: 'inv_suppliers', labelKey: 'item_suppliers', path: '/inventory/suppliers', permissions: ['supplier'] },
      { key: 'inv_items', labelKey: 'items', path: '/inventory/items', permissions: ['item'] },
      { key: 'inv_stock', labelKey: 'add_stock', path: '/inventory/stock', permissions: ['item_stock'] },
      { key: 'inv_issues', labelKey: 'issue_items', path: '/inventory/issues', permissions: ['issue_item'] },
      { key: 'inv_settings', labelKey: 'settings', path: '/inventory/settings', permissions: ['item'] },
    ],
  },

  // ═══════════════ Transport ═══════════════
  // PHP line 864-931: module_lib->hasActive('transport')
  {
    key: 'transport', labelKey: 'transport', icon: 'truck', module: 'transport', path: '/transport',
    permissions: ['routes', 'vehicle', 'assign_vehicle', 'routes_stops', 'trips', 'driver_trips', 'transport_notifications', 'transport_analytics', 'transport_maintenance'],
    children: [
      { key: 'transport_dashboard', labelKey: 'dashboard', path: '/transport', permissions: ['routes'] },
      { key: 'routes', labelKey: 'routes', path: '/transport/routes', permissions: ['routes'] },
      { key: 'vehicles', labelKey: 'vehicles', path: '/transport/vehicles', permissions: ['vehicle'] },
      { key: 'route_stops', labelKey: 'route_stops', path: '/transport/stops', permissions: ['routes'] },
      { key: 'vehicle_routes', labelKey: 'assign_vehicle', path: '/transport/vehicle-routes', permissions: ['assign_vehicle'] },
      { key: 'student_stops', labelKey: 'student_stops', path: '/transport/student-stops', permissions: ['routes'] },
      { key: 'trips', labelKey: 'trips', path: '/transport/trips', permissions: ['routes'] },
      { key: 'driver_trips', labelKey: 'driver_trips', path: '/transport/driver', permissions: ['routes'] },
      { key: 'transport_notifications', labelKey: 'transport_notifications', path: '/transport/notifications', permissions: ['routes'] },
      { key: 'vendors', labelKey: 'vendors', path: '/transport/vendors', permissions: ['transport_maintenance'] },
      { key: 'fleet_management', labelKey: 'fleet_management', path: '/transport/fleet', permissions: ['transport_maintenance'] },
    ],
  },

  // ═══════════════ Hostel ═══════════════
  // PHP line 932-966: module_lib->hasActive('hostel')
  {
    key: 'hostel', labelKey: 'hostel', icon: 'home', module: 'hostel', path: '/hostel',
    permissions: ['hostel_rooms', 'room_type', 'hostel'],
    children: [
      { key: 'hostel_dashboard', labelKey: 'dashboard', path: '/hostel', permissions: ['hostel'] },
      { key: 'hostel_list', labelKey: 'hostel', path: '/hostel/list', permissions: ['hostel'] },
      { key: 'hostel_rooms', labelKey: 'hostel_rooms', path: '/hostel/rooms', permissions: ['hostel_rooms'] },
      { key: 'room_type', labelKey: 'room_type', path: '/hostel/room-types', permissions: ['room_type'] },
      { key: 'hostel_allocations', labelKey: 'allocations', path: '/hostel/allocations', permissions: ['hostel_rooms'] },
      { key: 'hostel_allocation_list', labelKey: 'allocation_report', path: '/hostel/allocation-list', permissions: ['hostel_rooms'] },
      { key: 'hostel_attendance', labelKey: 'hostel_attendance', path: '/hostel/attendance', permissions: ['hostel'] },
      { key: 'hostel_complaints', labelKey: 'hostel_complaints', path: '/hostel/complaints', permissions: ['hostel'] },
      { key: 'hostel_visitors', labelKey: 'visitor_log', path: '/hostel/visitors', permissions: ['hostel'] },
      { key: 'hostel_staff_incharge', labelKey: 'Staff Incharge', path: '/hostel/staff-incharge', icon: '👥', permissions: ['hostel_staff_incharge'] },
    ],
  },

  // ═══════════════ Gate Pass ═══════════════
  {
    key: 'gate_pass', labelKey: 'Gate Pass', icon: 'clipboard', module: 'gate_pass',
    permissions: ['gate_pass'],
    children: [
      { key: 'gate_pass_new', labelKey: 'New Gate Pass', path: '/gate-pass', permissions: ['gate_pass'] },
      { key: 'gate_pass_list', labelKey: 'Gate Pass List', path: '/gate-pass/list', permissions: ['gate_pass'] },
      { key: 'gate_pass_checkin', labelKey: 'Check In', path: '/gate-pass/checkin', permissions: ['gate_pass'] },
      { key: 'gate_pass_stats', labelKey: 'Statistics', path: '/gate-pass/stats', permissions: ['gate_pass'] },
      { key: 'gate_pass_settings', labelKey: 'Gate Pass Settings', path: '/gate-pass/settings', permissions: ['gate_pass_settings'] },
    ],
  },

  // ═══════════════ Campus Network (OEL-185) ═══════════════
  // Not module-gated: it is its own permission, granted per role.
  { key: 'campus_network', labelKey: 'Campus Network', icon: 'globe', path: '/campus-network', permissions: ['campus_network'] },

  // ═══════════════ Student Counselling ═══════════════
  {
    key: 'student_counselling', labelKey: 'student_counselling', icon: 'users',
    permissions: ['student_counselling'],
    children: [
      { key: 'counselling_list', labelKey: 'counsellings', path: '/student-counselling', permissions: ['student_counselling'] },
      { key: 'counselling_topics', labelKey: 'topics', path: '/student-counselling/topics', permissions: ['student_counselling'] },
    ],
  },

  // ═══════════════ Student Complaints ═══════════════
  {
    key: 'student_complaints', labelKey: 'Student Complaints', icon: 'alert-circle',
    permissions: ['student_complaints'],
    children: [
      { key: 'complaints_list', labelKey: 'Complaints', path: '/complaints', permissions: ['student_complaints'] },
      { key: 'complaints_settings', labelKey: 'Complaint Settings', path: '/complaints/settings', permissions: ['student_complaints_settings'] },
    ],
  },

  // ═══════════════ Certificate ═══════════════
  // PHP line 967-1014: module_lib->hasActive('certificate')
  {

    key: 'certificate', labelKey: 'certificate', icon: 'file', module: 'certificate',
    permissions: ['student_certificate', 'generate_certificate', 'student_id_card', 'generate_id_card', 'staff_id_card', 'generate_staff_id_card'],
    children: [
      { key: 'cert_templates', labelKey: 'certificate', path: '/certificate/templates', permissions: ['student_certificate', 'generate_certificate'] },
      { key: 'cert_student_id', labelKey: 'student_id_card', path: '/certificate/student-id', permissions: ['student_id_card', 'generate_id_card'] },
      { key: 'cert_staff_id', labelKey: 'staff_id_card', path: '/certificate/staff-id', permissions: ['staff_id_card', 'generate_staff_id_card'] },
    ],
  },

  // ═══════════════ Front CMS ═══════════════
  // PHP line 1015-1066: module_lib->hasActive('front_cms')
  {
    key: 'front_cms', labelKey: 'front_cms', icon: 'globe', module: 'front_cms',
    permissions: ['event', 'gallery', 'notice', 'media_manager', 'pages', 'menus', 'banner_images'],
    children: [
      { key: 'event', labelKey: 'event', path: '/cms/events', permissions: ['event'] },
      { key: 'gallery', labelKey: 'gallery', path: '/cms/gallery', permissions: ['gallery'] },
      { key: 'cms_notice', labelKey: 'notice', path: '/cms/notice', permissions: ['notice'] },
      { key: 'media_manager', labelKey: 'media_manager', path: '/cms/media', permissions: ['media_manager'] },
      { key: 'pages', labelKey: 'page', path: '/cms/pages', permissions: ['pages'] },
      { key: 'menus', labelKey: 'menus', path: '/cms/menus', permissions: ['menus'] },
      { key: 'banner_images', labelKey: 'banner_images', path: '/cms/banner', permissions: ['banner_images'] },
    ],
  },

  // ═══════════════ Alumni ═══════════════
  // PHP line 1069-1090: module_lib->hasActive('alumni')
  {
    key: 'alumni', labelKey: 'alumni', icon: 'users', module: 'alumni',
    permissions: ['manage_alumni', 'events'],
    children: [
      { key: 'manage_alumni', labelKey: 'manage_alumni', path: '/alumni/manage', permissions: ['manage_alumni'] },
      { key: 'alumni_events', labelKey: 'events', path: '/alumni/events', permissions: ['events'] },
    ],
  },

  // ═══════════════ Reports ═══════════════
  // PHP line 1092-1260: module_lib->hasActive('reports')
  {
    key: 'reports', labelKey: 'reports', icon: 'pie-chart', module: 'reports',
    permissions: [
      'student_report', 'guardian_report', 'student_history', 'student_login_credential_report',
      'class_subject_report', 'admission_report', 'sibling_report', 'evaluation_report', 'student_profile',
      'fees_statement', 'balance_fees_report', 'fees_collection_report', 'online_fees_collection_report',
      'income_report', 'expense_report', 'payroll_report', 'income_group_report', 'expense_group_report',
      'attendance_report', 'staff_attendance_report', 'rank_report',
      'online_exam_wise_report', 'online_exams_report', 'online_exams_attempt_report', 'online_exams_rank_report',
      'syllabus_status_report', 'teacher_syllabus_status_report',
      'staff_report', 'book_issue_report', 'book_due_report', 'book_inventory_report',
      'stock_report', 'add_item_report', 'issue_inventory_report',
      'transport_report', 'hostel_report', 'alumni_report', 'user_log', 'audit_trail_report',
      'academic_report',
      // Lets hostel staff reach the Study Hours Report below. Children are
      // filtered on their own permissions, so this opens the Reports menu, not
      // the reports inside it (OEL-196).
      'hostel', 'study_hours_report',
    ],
    children: [
      { key: 'sms_report', labelKey: 'SMS Report', icon: 'message-square', path: '/reports/sms', permissions: ['sms_report'] },
      // Academic Reports — Theory / Objective mark sheets (OEL-193)
      { key: 'academic_report', labelKey: 'Academic Reports', icon: 'bar-chart', path: '/reports/academic', permissions: ['academic_report'] },
      // Study Hours Report — the same sheet as Hostel → Attendance (OEL-196)
      { key: 'study_hours_report', labelKey: 'Study Hours Report', icon: 'clock', path: '/reports/study-hours', module: 'hostel', permissions: ['study_hours_report', 'hostel'] },
      { key: 'student_report', labelKey: 'student_information', path: '/reports/student',
        permissions: ['student_report', 'guardian_report', 'student_history', 'student_login_credential_report',
          'class_subject_report', 'admission_report', 'sibling_report', 'evaluation_report', 'student_profile'] },
      { key: 'finance_report', labelKey: 'finance', path: '/reports/finance',
        permissions: ['fees_statement', 'balance_fees_report', 'fees_collection_report', 'online_fees_collection_report',
          'income_report', 'expense_report', 'payroll_report', 'income_group_report', 'expense_group_report'] },
      { key: 'attendance_report', labelKey: 'attendance', path: '/reports/attendance',
        permissions: ['attendance_report', 'staff_attendance_report'] },
      { key: 'exam_report', labelKey: 'examinations', path: '/reports/exam', permissions: ['rank_report'] },
      { key: 'online_exam_report', labelKey: 'online_examination', path: '/reports/online-exam', module: 'online_examination',
        permissions: ['online_exam_wise_report', 'online_exams_report', 'online_exams_attempt_report', 'online_exams_rank_report'] },
      { key: 'lesson_plan_report', labelKey: 'lesson_plan', path: '/reports/lesson-plan', module: 'lesson_plan',
        permissions: ['syllabus_status_report', 'teacher_syllabus_status_report'] },
      { key: 'hr_report', labelKey: 'human_resource', path: '/reports/hr', module: 'human_resource',
        permissions: ['staff_report', 'payroll_report'] },
      { key: 'library_report', labelKey: 'library', path: '/reports/library', module: 'library',
        permissions: ['book_issue_report', 'book_due_report', 'book_inventory_report'] },
      { key: 'inventory_report', labelKey: 'inventory', path: '/reports/inventory', module: 'inventory',
        permissions: ['stock_report', 'add_item_report', 'issue_inventory_report'] },
      { key: 'transport_report', labelKey: 'transport', path: '/reports/transport', module: 'transport',
        permissions: ['transport_report'] },
      { key: 'hostel_report', labelKey: 'hostel', path: '/reports/hostel', module: 'hostel',
        permissions: ['hostel_report'] },
      { key: 'alumni_report', labelKey: 'alumni', path: '/reports/alumni', module: 'alumni',
        permissions: ['alumni_report'] },
      { key: 'user_log', labelKey: 'user_log', path: '/reports/user-log', permissions: ['user_log'] },
      { key: 'audit_trail', labelKey: 'audit_trail', path: '/reports/audit', permissions: ['audit_trail_report'] },
    ],
  },

  // ═══════════════ System Settings ═══════════════
  // PHP line 1261-1385: module_lib->hasActive('system_settings')
  {
    key: 'settings', labelKey: 'system_settings', icon: 'settings', module: 'system_settings',
    permissions: ['general_setting', 'session_setting', 'notification_setting', 'sms_setting', 'email_setting',
      'payment_methods', 'print_header_footer', 'front_cms_setting', 'backup', 'languages', 'custom_fields', 'system_fields', 'student_profile_update'],
    children: [
      { key: 'general_setting', labelKey: 'general_setting', path: '/settings/general', permissions: ['general_setting'] },
      { key: 'session_setting', labelKey: 'session_setting', path: '/settings/session', permissions: ['session_setting'] },
      { key: 'notification_setting', labelKey: 'notification_setting', path: '/settings/notification', permissions: ['notification_setting'] },
      { key: 'sms_setting', labelKey: 'sms_setting', path: '/settings/sms', permissions: ['sms_setting'] },
      { key: 'email_setting', labelKey: 'email_setting', path: '/settings/email', permissions: ['email_setting'] },
      { key: 'payment_methods', labelKey: 'payment_methods', path: '/settings/payment', permissions: ['payment_methods'] },
      { key: 'print_header_footer', labelKey: 'print_header_footer', path: '/settings/print', permissions: ['print_header_footer'] },
      { key: 'front_cms_setting', labelKey: 'front_cms_setting', path: '/settings/cms', permissions: ['front_cms_setting'], module: 'front_cms' },
      { key: 'roles', labelKey: 'roles_permissions', path: '/settings/roles', superAdminOnly: true },
      { key: 'backup', labelKey: 'backup', path: '/settings/backup', permissions: ['backup'] },
      { key: 'language', labelKey: 'language', path: '/settings/language', permissions: ['languages'], permissionAction: 'can_add' },
      { key: 'users', labelKey: 'users', path: '/settings/users', permissions: ['user_status'] },
      { key: 'modules', labelKey: 'modules', path: '/settings/modules', superAdminOnly: true },
      { key: 'custom_fields', labelKey: 'custom_fields', path: '/settings/custom-fields', permissions: ['custom_fields'] },
      { key: 'captcha_setting', labelKey: 'captcha_setting', path: '/settings/captcha', superAdminOnly: true },
      { key: 'system_fields', labelKey: 'system_fields', path: '/settings/system-fields', permissions: ['system_fields'] },
      { key: 'student_profile_update', labelKey: 'student_profile_update', path: '/settings/profile-update', permissions: ['student_profile_update'] },
      { key: 'file_types', labelKey: 'file_types', path: '/settings/file-types', superAdminOnly: true },
      { key: 'activity_logs', labelKey: 'activity_log', path: '/settings/activity-logs', superAdminOnly: true },
      { key: 'system_update', labelKey: 'system_update', path: '/settings/update', superAdminOnly: true },
    ],
  },
];

// ─── User (Student/Parent) Sidebar ───
// Ported from: layout/student/header.php
export const USER_NAV: NavItem[] = [
  { key: 'dashboard', labelKey: 'dashboard', icon: 'home', path: '/dashboard' },
  { key: 'profile', labelKey: 'my_profile', icon: 'user', path: '/profile' },
  { key: 'my_inventory', labelKey: 'my_inventory', icon: 'box', path: '/my-inventory', module: 'inventory' },
  { key: 'fees', labelKey: 'fees', icon: 'dollar', path: '/fees', module: 'fees' },
  { key: 'class_timetable', labelKey: 'class_timetable', icon: 'calendar', path: '/timetable', module: 'class_timetable' },
  { key: 'lesson_plan', labelKey: 'lesson_plan', icon: 'book', path: '/lesson-plan', module: 'lesson_plan' },
  { key: 'syllabus_status', labelKey: 'syllabus_status', icon: 'check-circle', path: '/syllabus-status', module: 'syllabus_status' },
  { key: 'homework', labelKey: 'homework', icon: 'edit', path: '/homework', module: 'homework' },
  { key: 'online_exam', labelKey: 'online_examination', icon: 'globe', path: '/online-exam', module: 'online_examination' },
  {
    key: 'practice', labelKey: 'practice', icon: 'book',
    children: [
      { key: 'practice_questions', labelKey: 'practice_questions', path: '/practice/questions' },
      { key: 'create_exam', labelKey: 'create_exam', path: '/practice/create' },
      { key: 'my_performance', labelKey: 'my_performance', path: '/practice/analytics' },
      { key: 'my_mistakes', labelKey: 'my_mistakes', path: '/practice/errors' },
    ],
  },
  { key: 'apply_leave', labelKey: 'apply_leave', icon: 'check', path: '/leave', module: 'apply_leave' },
  {
    key: 'download_center', labelKey: 'download_center', icon: 'download', module: 'download_center',
    children: [
      { key: 'assignments', labelKey: 'assignments', path: '/downloads/assignments' },
      { key: 'study_material', labelKey: 'study_material', path: '/downloads/study-material' },
      { key: 'syllabus_dl', labelKey: 'syllabus', path: '/downloads/syllabus' },
      { key: 'other_downloads', labelKey: 'other_downloads', path: '/downloads/other' },
    ],
  },
  { key: 'attendance', labelKey: 'attendance', icon: 'check-circle', path: '/attendance', module: 'attendance' },
  {
    key: 'examinations', labelKey: 'examinations', icon: 'file', module: 'examinations',
    children: [
      { key: 'exam_schedule', labelKey: 'exam_schedule', path: '/exam/schedule' },
      { key: 'exam_result', labelKey: 'exam_result', path: '/exam/result' },
    ],
  },
  { key: 'notice_board', labelKey: 'notice_board', icon: 'mail', path: '/notices', module: 'notice_board' },
  { key: 'teacher_review', labelKey: 'teachers_review', icon: 'users', path: '/teachers', module: 'teachers_rating' },
  {
    key: 'library', labelKey: 'library', icon: 'book', module: 'library',
    children: [
      { key: 'books', labelKey: 'books', path: '/library/books' },
      { key: 'book_issued', labelKey: 'book_issued', path: '/library/issued' },
    ],
  },
  {
    key: 'transport', labelKey: 'transport', icon: 'truck', module: 'transport_routes',
    children: [
      { key: 'transport_routes', labelKey: 'transport_routes', path: '/transport/routes' },
      { key: 'my_transport', labelKey: 'my_transport', path: '/transport/my' },
    ],
  },
  { key: 'hostel', labelKey: 'hostel_rooms', icon: 'home', path: '/hostel', module: 'hostel_rooms' },
];
